// SD25: what remains of the client-side mirror of `workers/src/ffmpeg.rs`'s rung table.
//
// The rung selection, scaled-width and thumbnail-timestamp math that used to live here existed
// because the browser produced the published renditions itself (SD18/SD23). It no longer does
// (FR-P3c), so the server's table is the only one that decides anything about output. What is
// still a *client* decision is the capture bitrate hint handed to MediaRecorder — the browser
// picks the bitrate otherwise, and an unbounded pick is what makes a recording too big for
// FR-P12's remux budget.
//
// Pure — no WebCodecs, no DOM — so it stays unit-testable, and the bitrate rows can still be
// diffed by eye against the Rust table they came from.

/** The ladder tiers, highest first — the same bits/second values as `ffmpeg::LADDER`
 * (5000k/2800k/1400k/800k video). Only the video rate is used here; the server owns everything
 * else about the ladder. */
const LADDER_VIDEO_BITRATES: readonly { height: number; videoBitrateBps: number }[] = [
  { height: 1080, videoBitrateBps: 5_000_000 },
  { height: 720, videoBitrateBps: 2_800_000 },
  { height: 480, videoBitrateBps: 1_400_000 },
  { height: 360, videoBitrateBps: 800_000 },
];

/** FR-RW5: the `videoBitsPerSecond` hint handed to MediaRecorder, so browser-chosen capture
 * bitrates stay bounded — this is the bitstream the server packet-copies and every viewer
 * receives, and it is also what FR-P12's byte-size projection is measuring. 1.6× the ladder
 * tier's rate: enough headroom that a realtime hardware encoder (no two-pass, no lookahead)
 * doesn't visibly starve screen text, low enough that an hour of 1080p stays inside the
 * automatic-remux budget rather than needing to be asked for. */
export function captureBitrateBps(sourceHeight: number): number {
  const tier =
    LADDER_VIDEO_BITRATES.find((rung) => rung.height <= sourceHeight) ??
    LADDER_VIDEO_BITRATES[LADDER_VIDEO_BITRATES.length - 1];
  return Math.round(tier.videoBitrateBps * 1.6);
}
